/**
 * la-cards.js — single source of truth for the Love Always content card markup.
 *
 * The article/offering card HTML here is lifted verbatim from la-filter.js's
 * renderCard(), so the cards on the life-journey filter, the life-event pages,
 * and the life-event-topic pages are byte-identical by construction.
 *
 * Usage:
 *   window.LACards.render(item, {
 *     imageUrl,       // optional: pre-resolved image; falls back to resolveImage(item)
 *     imageFallback,  // optional: <img onerror> fallback src (article cards)
 *     authorLogo,     // optional: pre-resolved logo url; falls back to getAuthorLogo(item)
 *     isFavorited,    // optional: boolean
 *   })
 *
 * `item` is a filter-API result: { id, content_type, title, url, image_url,
 * preview_image_url, author, company_name, summary, description, price, category,
 * read_time, is_favorite }.
 */
(function () {
  "use strict";

  function escapeAttr(value) {
    if (!value) return "";
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  function truncateText(text, maxLength) {
    if (!text) return "";
    const stripped = text.replace(/<[^>]*>/g, "").trim();
    return stripped.length > maxLength ? stripped.substring(0, maxLength) + "..." : stripped;
  }

  function isValidImageUrl(url) {
    return typeof url === "string" && url.trim().length > 0;
  }

  var ARTICLE_PLACEHOLDER =
    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="600" height="400"%3E%3Crect fill="%23ffffff" width="600" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Montserrat, sans-serif" font-size="24" font-weight="600" fill="%23333"%3EArticle%3C/text%3E%3C/svg%3E';
  var OFFERING_PLACEHOLDER =
    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="600" height="400"%3E%3Crect fill="%23e0d6e8" width="600" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Montserrat, sans-serif" font-size="24" font-weight="600" fill="%23333"%3EProduct%3C/text%3E%3C/svg%3E';

  // Generic image resolution (preview > featured > image_url > type placeholder).
  // la-filter passes its own richer resolveDisplayImage result via opts.imageUrl;
  // la-life-events relies on this default.
  function resolveImage(item, type) {
    if (isValidImageUrl(item.preview_image_url)) return item.preview_image_url;
    if (isValidImageUrl(item.featured_image)) return item.featured_image;
    if (isValidImageUrl(item.image_url)) return item.image_url;
    return type === "article" ? ARTICLE_PLACEHOLDER : OFFERING_PLACEHOLDER;
  }

  function getAuthorLogo(item) {
    if (item.company_logo) return item.company_logo;
    if (item.logo) return item.logo;
    if (item.author_logo) return item.author_logo;
    if (item.author_image) return item.author_image;
    if (item.author && typeof item.author === "object") {
      if (item.author.image) return item.author.image;
      if (item.author.logo) return item.author.logo;
      if (item.author.avatar) return item.author.avatar;
    }
    return null;
  }

  function render(item, opts) {
    opts = opts || {};
    var type = item.content_type || (item.price ? "offering" : "article");
    var itemUrl = item.url || "#";

    var imageUrl = opts.imageUrl != null ? opts.imageUrl : resolveImage(item, type);
    var imageFallback = opts.imageFallback != null ? opts.imageFallback : "";
    var authorLogo = opts.authorLogo != null ? opts.authorLogo : getAuthorLogo(item);
    var isFavorited = !!(opts.isFavorited || item.is_favorite);

    var safeTitle = escapeAttr(item.title || "");
    // Cards show the short_title when set, falling back to the full title. The
    // full title is kept for image alt / aria / favourite data attributes.
    var safeDisplayTitle = escapeAttr(item.short_title || item.title || "");
    var safeUrl = escapeAttr(itemUrl);
    var safeImage = escapeAttr(imageUrl);
    var favoriteClasses = "favorite-btn" + (isFavorited ? " is-favorited" : "");
    var authorName = escapeAttr(
      item.author ||
        item.company_name ||
        (item.author && typeof item.author === "object" ? item.author.name : null) ||
        "Author"
    );

    if (type === "article") {
      return (
        '<article class="content-card article-card">' +
        '<a href="' + safeUrl + '" class="card-link-wrapper">' +
        '<div class="card-image">' +
        '<img src="' + safeImage + '" alt="' + safeTitle + '" loading="lazy" ' +
        'data-fallback="' + escapeAttr(imageFallback) + '" ' +
        'onerror="this.onerror=null; this.src=this.dataset.fallback || \'\';">' +
        '</div>' +
        '<div class="card-content">' +
        '<div class="company-badge">' +
        (authorLogo
          ? '<img src="' + escapeAttr(authorLogo) + '" alt="' + authorName + '">'
          : '<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#4A5568"/></svg>') +
        '<span>' + escapeAttr(typeof item.author === "object" && item.author.name ? item.author.name : item.author || item.company_name || "Author") + '</span>' +
        '</div>' +
        '<h3>' + safeDisplayTitle + '</h3>' +
        '<div class="card-footer">' +
        '<span class="card-link-text">Read more →</span>' +
        '<div class="card-meta">' +
        '<span>' + (item.read_time || 5) + ' mins read</span>' +
        '</div>' +
        '</div>' +
        '</div>' +
        '</a>' +
        '<button class="' + favoriteClasses + ' favorite-icon" type="button" aria-label="' + (isFavorited ? "Remove from favorites" : "Add to favorites") + '" aria-pressed="' + (isFavorited ? "true" : "false") + '" data-id="' + escapeAttr(item.id) + '" data-title="' + safeTitle + '" data-url="' + safeUrl + '" data-image="' + safeImage + '" data-type="article">' +
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="' + (isFavorited ? "currentColor" : "none") + '" stroke="currentColor" stroke-width="2">' +
        '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>' +
        '</svg>' +
        '</button>' +
        '</article>'
      );
    }

    return (
      '<article class="content-card offering-card">' +
      '<a href="' + safeUrl + '" class="card-link-wrapper">' +
      '<div class="card-image">' +
      '<div class="offering-card__bg-image" style="background-image: url(\'' + safeImage + '\');" role="img" aria-label="' + safeTitle + '"></div>' +
      '</div>' +
      '<div class="card-content">' +
      '<h3>' + safeDisplayTitle + '</h3>' +
      '<p class="card-description">' + truncateText(item.offering_caption || item.description || item.summary || "", 100) + '</p>' +
      '<div class="card-footer">' +
      '<span class="card-link-text card-link-primary">Explore offering →</span>' +
      '</div>' +
      '</div>' +
      '</a>' +
      '<button class="' + favoriteClasses + ' favorite-icon" type="button" aria-label="' + (isFavorited ? "Remove from favorites" : "Add to favorites") + '" aria-pressed="' + (isFavorited ? "true" : "false") + '" data-id="' + escapeAttr(item.id) + '" data-title="' + safeTitle + '" data-url="' + safeUrl + '" data-image="' + safeImage + '" data-type="offering">' +
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="' + (isFavorited ? "currentColor" : "none") + '" stroke="currentColor" stroke-width="2">' +
      '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>' +
      '</svg>' +
      '</button>' +
      '</article>'
    );
  }

  window.LACards = {
    render: render,
    escapeAttr: escapeAttr,
    truncateText: truncateText,
    resolveImage: resolveImage,
    getAuthorLogo: getAuthorLogo,
  };
})();
